import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n/I18nContext";
import { createFrequency } from "../services/frequency";
import { getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import { getClassesLabels } from "../utils/classesLabels";
import WizardSteps from "./shared/WizardSteps";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatCurrencyMZN } from "../utils/paymentHelpers";

const TOTAL_STEPS = 2;

const initialState = {
  description: "",
  code: "",
  duration: "",
  durationUnit: "Meses",
  registrationValue: "",
  recurigRegistrationValue: "",
  monthlyPayment: "",
  specialHourMonthlyValue: "",
};

// Split out of the former CursosLayer, same "hide cadastro from the menu"
// philosophy applied to CadastrarDespesaLayer/CadastrarProdutoLayer/etc.:
// this screen is only reachable via the "Adicionar" button on
// ListaClassesLayer, not from the sidebar (see App.js route
// `/classes/lista/cadastrar`). Editing an existing record stays on the list
// screen's own modal.
//
// The entity is called "Curso"/"Classe"/"Nível" depending on the logged-in
// user's sucursal (see src/utils/classesLabels.js) — the title and the
// "Cadastrar Outro"/back-to-list actions follow that same term.
const CadastrarClasseLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = getClassesLabels();
  const term = t(labels.singular);
  const showCodeAndDuration = labels.isCursos;

  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("classe.wizard.step.data"), t("classe.wizard.step.confirm")];

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    const missingRequired =
      !form.description ||
      !form.registrationValue ||
      !form.monthlyPayment ||
      (showCodeAndDuration && !form.duration);
    if (currentStep === 1 && missingRequired) {
      toast.error(t("classe.error.requiredFields"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createFrequency({
        description: form.description,
        ...(showCodeAndDuration
          ? { code: form.code, duration: Number(form.duration), durationUnit: form.durationUnit }
          : {}),
        registrationValue: Number(form.registrationValue) || 0,
        recurigRegistrationValue: Number(form.recurigRegistrationValue) || Number(form.registrationValue) || 0,
        monthlyPayment: Number(form.monthlyPayment) || 0,
        specialHourMonthlyValue: Number(form.specialHourMonthlyValue) || 0,
        createdBy: getUserInfo()?.id,
      });
      setDone(true);
    } catch (err) {
      toast.error(t("classe.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("classe.wizard.title", { term })}</h6>
            <p className='text-neutral-500'>{t("classe.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className={showCodeAndDuration ? "col-sm-6" : "col-sm-12"}>
                      <label className='form-label'>{t("classe.field.description")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input type='text' className='form-control' value={form.description} onChange={setField("description")} required />
                      </div>
                    </div>
                    {showCodeAndDuration && (
                      <div className='col-sm-3'>
                        <label className='form-label'>{t("turma.field.code")}</label>
                        <div className='icon-field'>
                          <span className='icon'>
                            <Icon icon='solar:hashtag-square-outline' />
                          </span>
                          <input type='text' className='form-control' value={form.code} onChange={setField("code")} />
                        </div>
                      </div>
                    )}
                    {showCodeAndDuration && (
                      <div className='col-sm-3'>
                        <label className='form-label'>{t("classe.field.duration")}</label>
                        <div className='d-flex align-items-center gap-2'>
                          <div className='icon-field flex-grow-1'>
                            <span className='icon'>
                              <Icon icon='solar:pen-2-outline' />
                            </span>
                            <input type='number' min='1' className='form-control' value={form.duration} onChange={setField("duration")} required />
                          </div>
                          <span>{t("classe.field.duration.months")}</span>
                        </div>
                      </div>
                    )}
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("classe.field.registrationValue")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.registrationValue} onChange={setField("registrationValue")} required />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("classe.field.renewalValue")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input
                          type='number'
                          min='0'
                          className='form-control'
                          value={form.recurigRegistrationValue}
                          onChange={setField("recurigRegistrationValue")}
                          placeholder={form.registrationValue}
                        />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("classe.field.monthlyPayment")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.monthlyPayment} onChange={setField("monthlyPayment")} required />
                      </div>
                    </div>
                    {hasSpecialHourSchedule() && (
                      <div className='col-sm-4'>
                        <label className='form-label'>{t("classe.field.specialHourMonthlyValue")}</label>
                        <div className='icon-field'>
                          <span className='icon'>
                            <Icon icon='solar:pen-2-outline' />
                          </span>
                          <input type='number' min='0' className='form-control' value={form.specialHourMonthlyValue} onChange={setField("specialHourMonthlyValue")} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("classe.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("classe.field.description")}:</strong> {form.description}
                    </li>
                    {showCodeAndDuration && (
                      <li>
                        <strong>{t("classe.field.duration")}:</strong> {form.duration} {form.durationUnit}
                      </li>
                    )}
                    <li>
                      <strong>{t("classe.field.registrationValue")}:</strong> {formatCurrencyMZN(form.registrationValue)}
                    </li>
                    <li>
                      <strong>{t("classe.field.monthlyPayment")}:</strong> {formatCurrencyMZN(form.monthlyPayment)}
                    </li>
                    {hasSpecialHourSchedule() && form.specialHourMonthlyValue && (
                      <li>
                        <strong>{t("classe.field.specialHourMonthlyValue")}:</strong> {formatCurrencyMZN(form.specialHourMonthlyValue)}
                      </li>
                    )}
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32' onClick={prevStep} disabled={submitting}>
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("classe.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("classe.action.createAnother")}
                    </button>
                    <button type='button' className='btn btn-primary-600 px-32' onClick={() => navigate("/classes/lista")}>
                      <i className='ri-arrow-left-line' /> {t(labels.submenu)}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarClasseLayer;
